// src/components/admin/AuditTrail.jsx
import React, { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../../api.js'
import { useAuthOptional } from '../AuthContext.jsx'
import {
  Search,
  RefreshCw,
  Filter,
  AlertTriangle,
  Shield,
  User,
  Clock,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  KeyRound,
  FileText,
  ShoppingCart,
  CreditCard,
  Settings,
  Eye,
  XCircle,
  CheckCircle2,
  AlertCircle,
  Download,
  Activity,
  Users,
  ReceiptText,
  Undo2,
  BookOpen,
  Trash2,
} from 'lucide-react'

function cx(...arr) {
  return arr.filter(Boolean).join(' ')
}

// EntityTimelineModal - timeline with diff
function EntityTimelineModal({ entityType, entityId, onClose }) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const d = await apiFetch(`/api/audit/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}?diff=true`)
        if (alive) setEvents(Array.isArray(d) ? d : (d.rows || []))
      } catch (e) {
        if (alive) setErr(String(e?.message || e))
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [entityType, entityId])

  function DiffView({ before, after, diff }) {
    if (!diff?.has_diff && !before && !after) return null
    const changed = diff?.changed || []
    const added = diff?.added || []
    const removed = diff?.removed || []
    if (!changed.length && !added.length && !removed.length) return (
      <div className="mt-2 text-xs text-slate-400">Aucun changement détecté</div>
    )
    return (
      <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
        {changed.map(c => (
          <div key={c.field} className="grid grid-cols-3 gap-px border-b border-slate-200 last:border-0">
            <div className="px-3 py-1.5 text-xs font-mono text-slate-500 bg-slate-100">{c.field}</div>
            <div className="px-3 py-1.5 text-xs font-mono bg-rose-50 text-rose-700 line-through truncate">{JSON.stringify(c.from)}</div>
            <div className="px-3 py-1.5 text-xs font-mono bg-emerald-50 text-emerald-700 truncate">{JSON.stringify(c.to)}</div>
          </div>
        ))}
        {added.map(c => (
          <div key={c.field} className="grid grid-cols-3 gap-px border-b border-slate-200 last:border-0">
            <div className="px-3 py-1.5 text-xs font-mono text-slate-500 bg-slate-100">{c.field}</div>
            <div className="px-3 py-1.5 text-xs bg-slate-100" />
            <div className="px-3 py-1.5 text-xs font-mono bg-emerald-50 text-emerald-700 truncate">{JSON.stringify(c.to)}</div>
          </div>
        ))}
        {removed.map(c => (
          <div key={c.field} className="grid grid-cols-3 gap-px border-b border-slate-200 last:border-0">
            <div className="px-3 py-1.5 text-xs font-mono text-slate-500 bg-slate-100">{c.field}</div>
            <div className="px-3 py-1.5 text-xs font-mono bg-rose-50 text-rose-700 line-through truncate">{JSON.stringify(c.from)}</div>
            <div className="px-3 py-1.5 text-xs bg-slate-100" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm overflow-y-auto py-8 px-4">
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Timeline</h2>
            <p className="text-xs text-slate-500 mt-0.5">{entityType} · {entityId}</p>
          </div>
          <button onClick={onClose} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>
          ) : err ? (
            <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">{err}</div>
          ) : events.length === 0 ? (
            <div className="text-center py-8 text-slate-500">Aucun événement trouvé</div>
          ) : (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200" />
              <div className="space-y-6">
                {events.map((ev, i) => (
                  <div key={ev.id || i} className="relative pl-10">
                    <div className="absolute left-2 top-1.5 h-4 w-4 rounded-full border-2 border-slate-900 bg-white" />
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-slate-900">{ev.action}</span>
                      <span className="text-xs text-slate-400">{ev.username || ev.user_id?.slice?.(0,8)}</span>
                      <span className="text-xs text-slate-400 ml-auto">{ev.created_at ? new Date(ev.created_at).toLocaleString('fr-FR') : ''}</span>
                    </div>
                    {ev.summary && <div className="text-xs text-slate-600">{ev.summary}</div>}
                    {ev.computed_diff && <DiffView before={ev.before_data} after={ev.after_data} diff={ev.computed_diff} />}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function fmtFR(d) {
  if (!d) return '—'
  const dt = new Date(d)
  if (Number.isNaN(dt.getTime())) return '—'
  return dt.toLocaleString('fr-FR')
}

/* =========================
 * UX helpers (lisible + anti-bruit)
 * ========================= */
function shortId(id, head = 8, tail = 4) {
  if (!id) return ''
  const s = String(id)
  if (s.length <= head + tail + 1) return s
  return `${s.slice(0, head)}…${s.slice(-tail)}`
}

function routePath(route) {
  if (!route) return '—'
  const s = String(route)
  const idx = s.indexOf('?')
  return idx >= 0 ? s.slice(0, idx) : s
}

function actionLabel(action) {
  const a = String(action || '').toUpperCase()

  const MAP = {
    // Auth/session
    ME_ACCESS: 'Session vérifiée (profil)',
    RECHECK_SUCCESS: 'Session renouvelée (OK)',
    RECHECK_UNAUTHORIZED: 'Session invalide (non autorisé)',
    LOGIN_SUCCESS: 'Connexion réussie',
    LOGIN_FAILED: 'Connexion échouée',
    LOGOUT: 'Déconnexion',

    // Sales / orders
    SALE_CREATED: 'Vente créée',
    SALE_PAID_CASH: 'Paiement encaissé (cash)',
    SALE_PAID_CREDIT: 'Paiement enregistré (crédit)',
    ORDER_CREATED: 'Commande créée',
    ORDER_UPDATED: 'Commande modifiée',
    ORDER_CANCELED: 'Commande annulée',

    // Devis / Quotes
    QUOTE_CREATED: 'Devis créé',
    QUOTE_UPDATED: 'Devis modifié',
    QUOTE_DELETED: 'Devis supprimé',
    QUOTE_SENT: 'Devis envoyé',
    QUOTE_ACCEPTED: 'Devis accepté',
    QUOTE_REFUSED: 'Devis refusé',
    QUOTE_CONVERTED: 'Devis converti en commande',

    // Avoirs / Credit Notes
    CREDIT_NOTE_CREATED: 'Avoir créé',
    CREDIT_NOTE_POSTED: 'Avoir validé (écriture comptable)',
    CREDIT_NOTE_CANCELLED: 'Avoir annulé',

    // Finance
    FINANCE_ENTRY_CREATED: 'Écriture journal créée',
    FINANCE_PERIOD_CREATED: 'Période financière créée',
    FINANCE_PERIOD_CLOSED: 'Période clôturée',
    FINANCE_PERIOD_REOPENED: 'Période réouverte',

    // Clients
    CUSTOMER_CREATED: 'Client créé',
    CUSTOMER_UPDATED: 'Client mis à jour',
    CUSTOMER_IMPORT_CSV: 'Import CSV clients',

    // Projets
    PROJECT_CREATED: 'Projet créé',
    PROJECT_STATUS_CHANGED: 'Statut projet modifié',
    PROJECT_DELETED: 'Projet supprimé',

    // Admin / settings
    INVOICE_TEMPLATE_UPDATED: 'Modèle de facture mis à jour',
    SETTINGS_UPDATED: 'Paramètres mis à jour',
  }

  if (MAP[a]) return MAP[a]
  if (a.startsWith('HTTP_')) return `Requête ${a.replace('HTTP_', '')}`

  return a
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase())
}

function buildSummary(row) {
  const a = String(row?.action || '').toUpperCase()
  const meta = row?.meta && typeof row.meta === 'object' ? row.meta : null
  const base = row?.summary ? String(row.summary) : ''

  // Enrichissements "SAFE" (pas d'infos perso client)
  if (a === 'SALE_CREATED' || a === 'SALE_PAID_CASH' || a === 'SALE_PAID_CREDIT') {
    const external = meta?.external_id || meta?.externalId || null
    const total = meta?.total_ttc ?? meta?.total ?? null
    const mode = meta?.payment_mode || meta?.paymentMode || null
    const cur = meta?.currency || 'XOF'

    let totalTxt = null
    if (total != null && total !== '' && !Number.isNaN(Number(total))) {
      totalTxt = `${Number(total).toLocaleString('fr-FR')} ${cur}`
    }

    const pieces = [
      actionLabel(a),
      external ? `• ${external}` : null,
      totalTxt ? `• ${totalTxt}` : null,
      mode ? `• ${mode}` : null,
    ].filter(Boolean)

    return pieces.join(' ')
  }

  if (a === 'LOGIN_SUCCESS') return 'Connexion réussie'
  if (a === 'LOGIN_FAILED') return base || 'Connexion échouée'
  if (a === 'ME_ACCESS') return 'Session vérifiée (profil)'
  if (a === 'RECHECK_SUCCESS') return 'Session renouvelée (OK)'
  if (a === 'RECHECK_UNAUTHORIZED') return 'Session invalide (non autorisé)'

  return base || actionLabel(a)
}

function riskBadge(risk) {
  const r = String(risk || 'LOW').toUpperCase()
  const base =
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1'
  if (r === 'HIGH') return (
    <span className={cx(base, 'bg-red-50 text-red-700 ring-red-200')}>
      <AlertTriangle size={12} />HIGH
    </span>
  )
  if (r === 'MEDIUM') return (
    <span className={cx(base, 'bg-amber-50 text-amber-800 ring-amber-200')}>
      <Shield size={12} />MEDIUM
    </span>
  )
  return (
    <span className={cx(base, 'bg-surface-primary text-content-primary ring-border')}>
      <Shield size={12} />LOW
    </span>
  )
}

/* =========================
 * ActionBadge (timeline-friendly)
 * - Couleur + icône selon action
 * - Fallback stable
 * ========================= */
function actionVariant(action) {
  const a = String(action || '').toUpperCase()

  // AUTH
  if (a === 'LOGIN_SUCCESS' || a === 'RECHECK_SUCCESS' || a === 'ME_ACCESS') return 'auth_ok'
  if (a === 'LOGIN_FAILED' || a === 'RECHECK_UNAUTHORIZED') return 'auth_bad'
  if (a === 'LOGOUT') return 'auth_neutral'

  // SALES/ORDERS
  if (a === 'SALE_CREATED' || a === 'ORDER_CREATED') return 'sale_create'
  if (a.startsWith('SALE_PAID') || a.includes('PAID')) return 'sale_paid'
  if (a.includes('CANCEL')) return 'danger'
  if (a.includes('DELETED') || a.includes('DELETE')) return 'danger'

  // DEVIS (bleu)
  if (a.startsWith('QUOTE_')) return 'quote'

  // AVOIRS (violet)
  if (a.startsWith('CREDIT_NOTE_')) return 'credit_note'

  // FINANCE (amber)
  if (a.startsWith('FINANCE_')) return 'finance'

  // CLIENTS
  if (a.startsWith('CUSTOMER_')) return 'customer'

  // PROJETS
  if (a.startsWith('PROJECT_')) return 'project'

  // ADMIN
  if (a.includes('TEMPLATE') || a.includes('SETTINGS') || a.includes('CONFIG')) return 'admin'

  // HTTP
  if (a.startsWith('HTTP_')) return 'http'

  return 'default'
}

function ActionBadge({ action }) {
  const v = actionVariant(action)
  const label = actionLabel(action)

  const base =
    'inline-flex items-center gap-2 rounded-xl px-2.5 py-1 text-[12px] font-semibold ring-1 whitespace-nowrap'
  const iconCls = 'opacity-90'

  let cls = cx(base, 'bg-surface-primary text-content-primary ring-border')
  let Icon = FileText

  switch (v) {
    case 'auth_ok':
      cls = cx(base, 'bg-emerald-50 text-emerald-800 ring-emerald-200')
      Icon = CheckCircle2
      break
    case 'auth_bad':
      cls = cx(base, 'bg-red-50 text-red-800 ring-red-200')
      Icon = XCircle
      break
    case 'auth_neutral':
      cls = cx(base, 'bg-surface-primary text-content-primary ring-border')
      Icon = KeyRound
      break
    case 'sale_create':
      cls = cx(base, 'bg-slate-50 text-slate-900 ring-slate-200')
      Icon = ShoppingCart
      break
    case 'sale_paid':
      cls = cx(base, 'bg-violet-50 text-violet-800 ring-violet-200')
      Icon = CreditCard
      break
    case 'admin':
      cls = cx(base, 'bg-amber-50 text-amber-900 ring-amber-200')
      Icon = Settings
      break
    case 'http':
      cls = cx(base, 'bg-surface-primary text-content-primary ring-border')
      Icon = Eye
      break
    case 'danger':
      cls = cx(base, 'bg-red-50 text-red-800 ring-red-200')
      Icon = AlertCircle
      break
    case 'quote':
      cls = cx(base, 'bg-blue-50 text-blue-800 ring-blue-200')
      Icon = ReceiptText
      break
    case 'credit_note':
      cls = cx(base, 'bg-violet-50 text-violet-800 ring-violet-200')
      Icon = Undo2
      break
    case 'finance':
      cls = cx(base, 'bg-amber-50 text-amber-900 ring-amber-200')
      Icon = BookOpen
      break
    case 'customer':
      cls = cx(base, 'bg-teal-50 text-teal-800 ring-teal-200')
      Icon = Users
      break
    case 'project':
      cls = cx(base, 'bg-indigo-50 text-indigo-800 ring-indigo-200')
      Icon = Activity
      break
    default:
      cls = cx(base, 'bg-surface-primary text-content-primary ring-border')
      Icon = FileText
      break
  }

  return (
    <span className={cls} title={String(action || '')}>
      <Icon size={14} className={iconCls} />
      {label}
    </span>
  )
}

export default function AuditTrail() {
  const auth = useAuthOptional()
  const user = auth?.user || null
  const isSuperAdmin = Array.isArray(user?.roles) && user.roles.includes('superadmin')

  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  const [rows, setRows] = useState([])
  const [actions, setActions] = useState([])
  const [actors, setActors] = useState([])
  const [stats, setStats] = useState(null)

  const [purgeMonths, setPurgeMonths] = useState(12)
  const [purgeLoading, setPurgeLoading] = useState(false)
  const [purgeResult, setPurgeResult] = useState(null)
  const [purgeError, setPurgeError] = useState(null)

  // Filtres
  const [q, setQ] = useState('')
  const [action, setAction] = useState('')
  const [riskLevel, setRiskLevel] = useState('')
  const [entityType, setEntityType] = useState('')
  const [entityId, setEntityId] = useState('')
  const [actorUserId, setActorUserId] = useState('')
  const [from, setFrom] = useState('') // datetime-local
  const [to, setTo] = useState('')     // datetime-local

  // Pagination
  const [limit, setLimit] = useState(50)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)

  // UX: masquer le bruit des 304 sur /api/auth/me
  const [hideNoise, setHideNoise] = useState(true)

  // Timeline modal
  const [timelineModal, setTimelineModal] = useState(null) // { entityType, entityId }

  const filters = useMemo(() => ({
    search: q.trim() || undefined,
    action: action || undefined,
    risk_level: riskLevel || undefined,
    entity_type: entityType.trim() || undefined,
    entity_id: entityId.trim() || undefined,
    actor_user_id: actorUserId.trim() || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(to).toISOString() : undefined,
    limit,
    offset,
  }), [q, action, riskLevel, entityType, entityId, actorUserId, from, to, limit, offset])

  async function loadMeta() {
    try {
      const [actionsData, actorsData, statsData] = await Promise.all([
        apiFetch('/api/audit/actions').catch(() => ({ rows: [] })),
        apiFetch('/api/audit/actors').catch(() => ({ rows: [] })),
        apiFetch('/api/audit/stats').catch(() => null),
      ])
      setActions(actionsData?.rows || [])
      setActors(actorsData?.rows || [])
      setStats(statsData || null)
    } catch (e) {
      console.warn('[audit.meta] failed:', e?.message || e)
    }
  }

  async function loadRows() {
    setLoading(true)
    setErr(null)
    try {
      const params = new URLSearchParams()
      Object.entries(filters).forEach(([k, v]) => {
        if (v === undefined || v === null || v === '') return
        params.set(k, String(v))
      })
      const data = await apiFetch(`/api/audit?${params.toString()}`)
      setRows(data?.rows || [])
      setHasMore(!!data?.has_more)
    } catch (e) {
      setErr(e?.message || 'Erreur chargement audit')
      setRows([])
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }

  function resetFilters() {
    setQ('')
    setAction('')
    setRiskLevel('')
    setEntityType('')
    setEntityId('')
    setActorUserId('')
    setFrom('')
    setTo('')
    setLimit(50)
    setOffset(0)
  }

  function buildExportUrl() {
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '' || k === 'limit' || k === 'offset') return
      params.set(k, String(v))
    })
    return `/api/audit/export.csv?${params.toString()}`
  }

  async function handlePurge() {
    const months = Math.min(Math.max(parseInt(purgeMonths, 10) || 12, 1), 120)
    const confirmed = window.confirm(
      `Confirmer la purge des logs d'audit antérieurs à ${months} mois ?\n\nCette action est irréversible.`
    )
    if (!confirmed) return
    setPurgeLoading(true)
    setPurgeResult(null)
    setPurgeError(null)
    try {
      const data = await apiFetch('/api/audit/purge', {
        method: 'POST',
        body: JSON.stringify({ months }),
      })
      setPurgeResult(data)
      loadRows()
    } catch (e) {
      setPurgeError(e?.message || 'Erreur lors de la purge')
    } finally {
      setPurgeLoading(false)
    }
  }

  // Load initial
  useEffect(() => {
    loadMeta()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reload on filters change
  useEffect(() => {
    loadRows()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.search,
    filters.action,
    filters.risk_level,
    filters.entity_type,
    filters.entity_id,
    filters.actor_user_id,
    filters.from,
    filters.to,
    filters.limit,
    filters.offset,
  ])

  const visibleRows = useMemo(() => {
    if (!hideNoise) return rows
    return rows.filter(r => {
      const isMe = (r.route || '').includes('/api/auth/me')
      const is304 = Number(r.status_code) === 304
      return !(isMe && is304)
    })
  }, [rows, hideNoise])

  const canPrev = offset > 0
  const canNext = hasMore

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-xl font-extrabold text-content-primary">Audit &amp; Traçabilité</div>
          <div className="text-sm text-content-secondary">
            Journal des actions: qui a fait quoi, quand, et sur quelle entité.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <a
            href={buildExportUrl()}
            download
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-semibold ring-1 transition-colors bg-surface-card text-content-primary ring-border hover:bg-surface-primary"
            title="Exporter les résultats filtrés en CSV"
          >
            <Download size={16} />
            CSV
          </a>
          <button
            type="button"
            onClick={() => { loadMeta(); loadRows() }}
            className={cx(
              'inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-semibold ring-1 transition-colors',
              'bg-surface-card text-content-primary ring-border hover:bg-surface-primary'
            )}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Rafraîchir
          </button>
        </div>
      </div>

      {/* Bandeau stats 24h */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="rounded-2xl bg-surface-card ring-1 ring-border p-3 flex items-center gap-3">
            <div className="rounded-xl bg-slate-100 p-2"><Activity size={18} className="text-slate-600" /></div>
            <div>
              <div className="text-[11px] font-semibold text-content-secondary">Événements 24h</div>
              <div className="text-xl font-extrabold text-content-primary">{stats.total_24h ?? '—'}</div>
            </div>
          </div>
          <div className="rounded-2xl bg-surface-card ring-1 ring-border p-3 flex items-center gap-3">
            <div className="rounded-xl bg-red-100 p-2"><AlertTriangle size={18} className="text-red-600" /></div>
            <div>
              <div className="text-[11px] font-semibold text-content-secondary">HIGH 24h</div>
              <div className={cx('text-xl font-extrabold', (stats.high_24h || 0) > 0 ? 'text-red-700' : 'text-content-primary')}>{stats.high_24h ?? '—'}</div>
            </div>
          </div>
          <div className="rounded-2xl bg-surface-card ring-1 ring-border p-3 flex items-center gap-3">
            <div className="rounded-xl bg-amber-100 p-2"><Shield size={18} className="text-amber-600" /></div>
            <div>
              <div className="text-[11px] font-semibold text-content-secondary">Échecs connexion 24h</div>
              <div className={cx('text-xl font-extrabold', (stats.login_failed_24h || 0) > 0 ? 'text-amber-700' : 'text-content-primary')}>{stats.login_failed_24h ?? '—'}</div>
            </div>
          </div>
          <div className="rounded-2xl bg-surface-card ring-1 ring-border p-3">
            <div className="text-[11px] font-semibold text-content-secondary mb-1">Dernière action admin</div>
            {stats.last_admin_action ? (
              <>
                <div className="text-[12px] font-bold text-content-primary truncate">{stats.last_admin_action.actor}</div>
                <div className="text-[11px] text-content-secondary truncate">{actionLabel(stats.last_admin_action.action)}</div>
                <div className="text-[11px] text-content-muted">{stats.last_admin_action.at ? new Date(stats.last_admin_action.at).toLocaleString('fr-FR') : '—'}</div>
              </>
            ) : <div className="text-[12px] text-content-muted">—</div>}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-2xl bg-surface-card ring-1 ring-border p-3 sm:p-4 mb-4">
        <div className="flex items-center gap-2 text-[12px] font-semibold text-content-primary mb-3">
          <Filter size={14} />
          Filtres
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {/* Search */}
          <label className="block">
            <div className="text-[11px] font-semibold text-content-secondary mb-1">Recherche</div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-2.5 text-content-muted" />
              <input
                value={q}
                onChange={(e) => { setOffset(0); setQ(e.target.value) }}
                placeholder="action, username, route, entity..."
                className="w-full rounded-xl border border-border bg-surface-card pl-9 pr-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-slate-100"
              />
            </div>
          </label>

          {/* Action */}
          <label className="block">
            <div className="text-[11px] font-semibold text-content-secondary mb-1">Action</div>
            <select
              value={action}
              onChange={(e) => { setOffset(0); setAction(e.target.value) }}
              className="w-full rounded-xl border border-border bg-surface-card px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-slate-100"
            >
              <option value="">Toutes</option>
              {actions.map((a) => (
                <option key={a.action} value={a.action}>
                  {a.action} ({a.count})
                </option>
              ))}
            </select>
          </label>

          {/* Entity type */}
          <label className="block">
            <div className="text-[11px] font-semibold text-content-secondary mb-1">Entity type</div>
            <input
              value={entityType}
              onChange={(e) => { setOffset(0); setEntityType(e.target.value) }}
              placeholder="order, payment, customer..."
              className="w-full rounded-xl border border-border bg-surface-card px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-slate-100"
            />
          </label>

          {/* Entity id */}
          <label className="block">
            <div className="text-[11px] font-semibold text-content-secondary mb-1">Entity id</div>
            <input
              value={entityId}
              onChange={(e) => { setOffset(0); setEntityId(e.target.value) }}
              placeholder="uuid / external_id / id..."
              className="w-full rounded-xl border border-border bg-surface-card px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-slate-100"
            />
          </label>

          {/* Actor dropdown */}
          <label className="block">
            <div className="text-[11px] font-semibold text-content-secondary mb-1">Acteur</div>
            <select
              value={actorUserId}
              onChange={(e) => { setOffset(0); setActorUserId(e.target.value) }}
              className="w-full rounded-xl border border-border bg-surface-card px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-slate-100"
            >
              <option value="">Tous</option>
              {actors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username || a.id} ({a.count})
                </option>
              ))}
            </select>
          </label>

          {/* Risk level */}
          <label className="block">
            <div className="text-[11px] font-semibold text-content-secondary mb-1">Niveau de risque</div>
            <select
              value={riskLevel}
              onChange={(e) => { setOffset(0); setRiskLevel(e.target.value) }}
              className="w-full rounded-xl border border-border bg-surface-card px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-slate-100"
            >
              <option value="">Tous</option>
              <option value="HIGH">🔴 HIGH</option>
              <option value="MEDIUM">🟡 MEDIUM</option>
              <option value="LOW">🟢 LOW</option>
            </select>
          </label>

          {/* From */}
          <label className="block">
            <div className="text-[11px] font-semibold text-content-secondary mb-1">Du</div>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => { setOffset(0); setFrom(e.target.value) }}
              className="w-full rounded-xl border border-border bg-surface-card px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-slate-100"
            />
          </label>

          {/* To */}
          <label className="block">
            <div className="text-[11px] font-semibold text-content-secondary mb-1">Au</div>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => { setOffset(0); setTo(e.target.value) }}
              className="w-full rounded-xl border border-border bg-surface-card px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-slate-100"
            />
          </label>

          {/* Limit */}
          <label className="block">
            <div className="text-[11px] font-semibold text-content-secondary mb-1">Page size</div>
            <select
              value={limit}
              onChange={(e) => { setOffset(0); setLimit(parseInt(e.target.value, 10)) }}
              className="w-full rounded-xl border border-border bg-surface-card px-3 py-2 text-[13px] outline-none focus:ring-2 focus:ring-slate-100"
            >
              {[25, 50, 100, 200].map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => { setOffset(0); loadRows() }}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-semibold bg-slate-900 text-white hover:bg-slate-950 transition-colors"
          >
            <Search size={16} />
            Appliquer
          </button>

          <button
            type="button"
            onClick={() => { resetFilters() }}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-semibold bg-surface-card text-content-primary ring-1 ring-border hover:bg-surface-primary transition-colors"
          >
            Réinitialiser
          </button>

          <button
            type="button"
            onClick={() => setHideNoise(v => !v)}
            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-semibold bg-surface-card text-content-primary ring-1 ring-border hover:bg-surface-primary transition-colors"
            title="Masque/Affiche les 304 sur /api/auth/me (bruit de cache/polling)"
          >
            {hideNoise ? 'Afficher bruit (304 /me)' : 'Masquer bruit'}
          </button>

          <div className="ml-auto text-[12px] text-content-secondary flex items-center gap-2">
            <Clock size={14} className="text-content-muted" />
            <span>Heure locale • {new Date().toLocaleString('fr-FR')}</span>
          </div>
        </div>
      </div>

      {/* Errors */}
      {err && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl bg-surface-card ring-1 ring-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border-light flex items-center justify-between">
          <div className="text-[12px] font-semibold text-content-primary">
            Événements {loading ? '• chargement...' : ''}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!canPrev}
              onClick={() => setOffset(o => Math.max(0, o - limit))}
              className={cx(
                'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-semibold ring-1 transition-colors',
                canPrev ? 'bg-surface-card text-content-primary ring-border hover:bg-surface-primary' : 'bg-surface-primary text-content-muted ring-border cursor-not-allowed'
              )}
            >
              <ChevronLeft size={14} />
              Prev
            </button>

            <button
              type="button"
              disabled={!canNext}
              onClick={() => setOffset(o => o + limit)}
              className={cx(
                'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-semibold ring-1 transition-colors',
                canNext ? 'bg-surface-card text-content-primary ring-border hover:bg-surface-primary' : 'bg-surface-primary text-content-muted ring-border cursor-not-allowed'
              )}
            >
              Next
              <ChevronRight size={14} />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead className="bg-surface-primary text-content-secondary">
              <tr className="text-left">
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Actor</th>
                <th className="px-4 py-3 font-semibold">Action</th>
                <th className="px-4 py-3 font-semibold">Entité</th>
                <th className="px-4 py-3 font-semibold">Route</th>
                <th className="px-4 py-3 font-semibold">Risque</th>
                <th className="px-4 py-3 font-semibold">Détails</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {!loading && visibleRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-content-secondary">
                    Aucun événement pour ces filtres.
                  </td>
                </tr>
              )}

              {visibleRows.map((r) => {
                const actorLabel = r.actor_username || r.actor_user_id || '—'
                const entityLabel = r.entity_type
                  ? `${r.entity_type}${r.entity_id ? ` • ${shortId(r.entity_id)}` : ''}`
                  : (r.entity_id ? shortId(r.entity_id) : '—')

                const details = buildSummary(r)
                const hasPayload = !!(r.before_data || r.after_data || r.meta || r.ip)

                return (
                  <tr key={r.id} className="hover:bg-surface-primary/60">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-semibold text-content-primary">{fmtFR(r.created_at)}</div>
                      <div className="text-[11px] text-content-secondary">{r.method || '—'} • {r.status_code ?? '—'}</div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-content-muted" />
                        <div className="min-w-0">
                          <div className="font-semibold text-content-primary truncate">{actorLabel}</div>
                          <div className="text-[11px] text-content-secondary truncate">
                            {(r.actor_roles && r.actor_roles.length) ? r.actor_roles.join(' • ') : '—'}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <ActionBadge action={r.action} />
                      </div>
                      <div className="text-[11px] text-content-secondary mt-1">
                        {r.action}
                        {r.request_id ? ` • ${shortId(r.request_id, 8, 4)}` : ''}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <div className="font-semibold text-content-primary">{entityLabel}</div>
                      {r.entity_type && r.entity_id && (
                        <button
                          onClick={() => setTimelineModal({ entityType: r.entity_type, entityId: r.entity_id })}
                          className="ml-1 inline-flex items-center gap-0.5 rounded-lg px-2 py-0.5 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                          title="Voir la timeline"
                        >
                          <ExternalLink size={11} /> timeline
                        </button>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <div className="text-content-primary">{routePath(r.route)}</div>
                      <div className="text-[11px] text-content-secondary truncate max-w-[320px]">
                        {r.user_agent ? `UA#${String(r.user_agent).slice(0, 16)}` : '—'}
                      </div>
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {riskBadge(r.risk_level)}
                    </td>

                    <td className="px-4 py-3">
                      <div className="text-content-primary">{details || '—'}</div>

                      {hasPayload && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-[11px] font-semibold text-content-secondary hover:text-content-primary">
                            voir JSON
                          </summary>
                          <pre className="mt-2 text-[11px] bg-slate-900 text-slate-100 rounded-xl p-3 overflow-auto max-h-56">
{JSON.stringify(
  {
    before: r.before_data || null,
    after: r.after_data || null,
    meta: r.meta || null,
    ip: r.ip || null,
  },
  null,
  2
)}
                          </pre>
                        </details>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Footer pagination */}
        <div className="px-4 py-3 border-t border-border-light flex items-center justify-between">
          <div className="text-[12px] text-content-secondary">
            Offset: <span className="font-semibold text-content-primary">{offset}</span> •
            Page size: <span className="font-semibold text-content-primary">{limit}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!canPrev}
              onClick={() => setOffset(o => Math.max(0, o - limit))}
              className={cx(
                'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-semibold ring-1 transition-colors',
                canPrev ? 'bg-surface-card text-content-primary ring-border hover:bg-surface-primary' : 'bg-surface-primary text-content-muted ring-border cursor-not-allowed'
              )}
            >
              <ChevronLeft size={14} />
              Prev
            </button>
            <button
              type="button"
              disabled={!canNext}
              onClick={() => setOffset(o => o + limit)}
              className={cx(
                'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-semibold ring-1 transition-colors',
                canNext ? 'bg-surface-card text-content-primary ring-border hover:bg-surface-primary' : 'bg-surface-primary text-content-muted ring-border cursor-not-allowed'
              )}
            >
              Next
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Quick tips */}
      <div className="mt-4 text-[12px] text-content-secondary">
        Astuce: pour avoir un filtre par période, remplis <strong>Du</strong> et <strong>Au</strong>. <br />
        Astuce 2: utilise <strong>Entity type</strong> + <strong>Entity id</strong> pour reconstituer la timeline d'une commande ou d'un paiement.
      </div>

      {isSuperAdmin && (
        <div className="mt-4 rounded-2xl bg-red-50 ring-1 ring-red-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 size={16} className="text-red-600" />
            <span className="text-[13px] font-bold text-red-800">Purge des logs d'audit</span>
            <span className="ml-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700 ring-1 ring-red-200">superadmin</span>
          </div>
          <p className="text-[12px] text-red-700 mb-3">
            Supprime définitivement les entrées d'audit plus anciennes que le nombre de mois indiqué. Cette action est irréversible.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-[12px] font-semibold text-red-800">
              Rétention :
              <input
                type="number"
                min={1}
                max={120}
                value={purgeMonths}
                onChange={(e) => setPurgeMonths(e.target.value)}
                className="w-20 rounded-xl border border-red-300 bg-white px-3 py-1.5 text-[13px] text-slate-900 outline-none focus:ring-2 focus:ring-red-200"
              />
              mois
            </label>
            <button
              type="button"
              onClick={handlePurge}
              disabled={purgeLoading}
              className={cx(
                'inline-flex items-center gap-2 rounded-xl px-4 py-2 text-[13px] font-semibold transition-colors',
                purgeLoading
                  ? 'bg-red-200 text-red-400 cursor-not-allowed'
                  : 'bg-red-600 text-white hover:bg-red-700'
              )}
            >
              <Trash2 size={14} />
              {purgeLoading ? 'Purge en cours...' : `Purger les logs > ${purgeMonths} mois`}
            </button>
          </div>
          {purgeResult && (
            <div className="mt-3 rounded-xl bg-white border border-red-200 px-4 py-2 text-[12px] text-red-800">
              Purge terminée : <strong>{purgeResult.deleted}</strong> entr{purgeResult.deleted === 1 ? 'ée' : 'ées'} supprimée{purgeResult.deleted === 1 ? '' : 's'} (rétention {purgeResult.months} mois).
            </div>
          )}
          {purgeError && (
            <div className="mt-3 rounded-xl bg-white border border-red-300 px-4 py-2 text-[12px] text-red-700">
              Erreur : {purgeError}
            </div>
          )}
        </div>
      )}

      {timelineModal && (
        <EntityTimelineModal
          entityType={timelineModal.entityType}
          entityId={timelineModal.entityId}
          onClose={() => setTimelineModal(null)}
        />
      )}
    </div>
  )
}
